import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import {
  Contact, Search, ChevronRight, Loader2, Users, CreditCard, Banknote, Trash2,
  Store, ArrowLeft, AlertCircle, Wallet, Activity, ShieldCheck,
} from "lucide-react";

const Field = ({ label, value, mono = false, className = "" }) => (
  <div className={className}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`mt-0.5 text-sm ${mono ? "font-mono" : ""}`}>{value || <em className="text-muted-foreground">—</em>}</div>
  </div>
);

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null); // customer_mobile

  const load = async (q = "") => {
    setLoading(true);
    try {
      const url = q ? `/admin/customers?search=${encodeURIComponent(q)}&limit=200` : "/admin/customers?limit=200";
      const { data } = await api.get(url);
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <DashboardLayout>
      {!picked ? (
        <>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <span className="overline">Customer Vault</span>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 flex items-center gap-2">
                <Contact className="h-7 w-7 text-primary" strokeWidth={1.5} /> Customers
              </h1>
              <p className="text-muted-foreground mt-2 max-w-xl">
                Cross-retailer view of every customer's saved beneficiaries and BBPS consumer cards. Customer identity is keyed by mobile number.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="customer-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  onKeyDown={(e) => e.key === "Enter" && load(search)}
                  placeholder="Search by mobile"
                  className="pl-8 w-48 font-mono"
                />
              </div>
              <Button variant="outline" onClick={() => load(search)} data-testid="customer-search-btn">Search</Button>
            </div>
          </div>

          <div className="mt-8 surface-card overflow-hidden" data-testid="customers-table">
            <div className="px-5 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">{items.length} customer{items.length === 1 ? "" : "s"}</h3>
              <span className="text-xs text-muted-foreground">Sorted by latest activity</span>
            </div>
            {loading ? (
              <div className="p-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center">
                <Contact className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
                <p className="mt-3 text-sm text-muted-foreground">No customers yet. They'll appear here as retailers add beneficiaries or consumer cards.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="p-3 pl-5">Mobile</th>
                    <th className="p-3">Beneficiaries</th>
                    <th className="p-3">Consumer cards</th>
                    <th className="p-3">Retailers</th>
                    <th className="p-3">Last activity</th>
                    <th className="p-3 text-right pr-5"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.customer_mobile} className="border-b border-border/50 last:border-0 hover:bg-accent/30 cursor-pointer"
                      onClick={() => setPicked(c.customer_mobile)} data-testid={`customer-row-${c.customer_mobile}`}>
                      <td className="p-3 pl-5 font-mono">+91 {c.customer_mobile}</td>
                      <td className="p-3"><Badge variant={c.bene_count > 0 ? "default" : "outline"} className="font-mono"><Users className="h-3 w-3 mr-1" />{c.bene_count}</Badge></td>
                      <td className="p-3"><Badge variant={c.card_count > 0 ? "default" : "outline"} className="font-mono"><CreditCard className="h-3 w-3 mr-1" />{c.card_count}</Badge></td>
                      <td className="p-3"><Badge variant="outline" className="font-mono"><Store className="h-3 w-3 mr-1" />{c.retailer_count}</Badge></td>
                      <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{formatTime(c.last_activity)}</td>
                      <td className="p-3 pr-5 text-right text-muted-foreground"><ChevronRight className="h-4 w-4 inline" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <CustomerDetail mobile={picked} onBack={() => { setPicked(null); load(search); }} />
      )}
    </DashboardLayout>
  );
}

function CustomerDetail({ mobile, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers/${mobile}`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mobile]);

  const deleteBene = async (id) => {
    if (!window.confirm("Delete this beneficiary? Retailer will lose access immediately.")) return;
    setActingId(id);
    try {
      await api.delete(`/admin/customers/beneficiaries/${id}`);
      toast.success("Beneficiary deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    } finally { setActingId(null); }
  };

  const deleteCard = async (id) => {
    if (!window.confirm("Delete this consumer card?")) return;
    setActingId(id);
    try {
      await api.delete(`/admin/customers/consumer-cards/${id}`);
      toast.success("Card deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    } finally { setActingId(null); }
  };

  if (loading || !data) {
    return <div className="surface-card p-10 flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading…</div>;
  }

  return (
    <div data-testid="customer-detail">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3" data-testid="customer-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to customers
      </button>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Customer · +91 {mobile}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 font-mono">
            {data.kyc?.full_name ? data.kyc.full_name : `+91 ${mobile}`}
          </h1>
          {data.kyc?.full_name && (
            <div className="text-sm text-muted-foreground font-mono mt-1">+91 {mobile}</div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <StatPill icon={Wallet} label="Wallet balance" value={formatINR(data.stats?.wallet_balance || 0)} />
          <StatPill icon={Users} label="Beneficiaries" value={data.stats.bene_count} />
          <StatPill icon={CreditCard} label="Cards" value={data.stats.card_count} />
          <StatPill icon={Store} label="Retailers" value={data.stats.retailer_count} />
          <StatPill icon={Activity} label="Txns" value={data.stats?.txn_count || 0} />
        </div>
      </div>

      {/* KYC summary card */}
      {data.kyc ? (
        <div className="mt-6 surface-card p-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-display text-lg font-semibold flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-success" /> KYC details
            </div>
            <Badge variant={data.kyc.status === "verified" ? "default" : "outline"} className="text-[10px]">
              {data.kyc.status?.toUpperCase()}
            </Badge>
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
            <Field label="Full name" value={data.kyc.full_name} />
            <Field label="PAN" value={data.kyc.pan} mono />
            <Field label="Aadhaar (masked)" value={data.kyc.aadhaar_masked || data.kyc.aadhaar} mono />
            <Field label="DOB" value={data.kyc.dob} />
            <Field label="Email" value={data.kyc.email} mono />
            <Field label="Address" value={data.kyc.address} className="sm:col-span-2 lg:col-span-3" />
            <Field label="Performed by" value={
              data.kyc.performed_by_user
                ? `${data.kyc.performed_by_user.full_name} (${data.kyc.performed_by_user.email})`
                : "—"
            } className="sm:col-span-2 lg:col-span-2" />
            <Field label="Performed at" value={formatTime(data.kyc.created_at || data.kyc.verified_at)} />
          </div>
        </div>
      ) : (
        <div className="mt-6 surface-card p-5 text-sm text-muted-foreground">
          <ShieldCheck className="h-4 w-4 inline mr-1 opacity-50" /> No KYC submitted yet.
        </div>
      )}

      <Tabs defaultValue="timeline" className="mt-8">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline ({(data.wallet_ledger?.length || 0) + (data.transactions?.length || 0) + (data.topups?.length || 0)})</TabsTrigger>
          <TabsTrigger value="beneficiaries" data-testid="tab-beneficiaries">Beneficiaries ({data.stats.bene_count})</TabsTrigger>
          <TabsTrigger value="cards" data-testid="tab-cards">Saved cards ({data.stats.card_count})</TabsTrigger>
          <TabsTrigger value="wallet" data-testid="tab-wallet">Wallet ledger ({data.wallet_ledger?.length || 0})</TabsTrigger>
          <TabsTrigger value="txns" data-testid="tab-txns">All txns ({data.stats?.txn_count || 0})</TabsTrigger>
          <TabsTrigger value="topups" data-testid="tab-topups">Top-ups ({data.stats?.topup_count || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="mt-6">
          <CombinedTimeline data={data} />
        </TabsContent>

        <TabsContent value="beneficiaries" className="mt-6">
          {data.beneficiaries.length === 0 ? (
            <Empty icon={Users} text="No beneficiaries for this customer." />
          ) : (
            <div className="surface-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="p-3 pl-5">Beneficiary</th>
                    <th className="p-3">Account / UPI</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Retailer</th>
                    <th className="p-3">Added</th>
                    <th className="p-3 text-right pr-5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.beneficiaries.map((b) => (
                    <tr key={b.id} className="border-b border-border/50 last:border-0 hover:bg-accent/30" data-testid={`bene-row-${b.id}`}>
                      <td className="p-3 pl-5">
                        <div className="font-medium">{b.nickname || b.name || "—"}</div>
                        {b.ifsc && <div className="text-[11px] font-mono text-muted-foreground mt-0.5">IFSC {b.ifsc}</div>}
                      </td>
                      <td className="p-3 font-mono text-xs">{b.account_number || b.upi_id || "—"}</td>
                      <td className="p-3"><Badge variant="outline" className="text-[10px]">{b.type || "bank"}</Badge></td>
                      <td className="p-3 text-xs">{b.retailer ? <>{b.retailer.full_name}<div className="text-[10px] text-muted-foreground">{b.retailer.email}</div></> : "—"}</td>
                      <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{formatTime(b.created_at)}</td>
                      <td className="p-3 pr-5 text-right">
                        <Button size="sm" variant="ghost" onClick={() => deleteBene(b.id)} disabled={actingId === b.id}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-bene-${b.id}`}>
                          {actingId === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="cards" className="mt-6">
          {data.consumer_cards.length === 0 ? (
            <Empty icon={CreditCard} text="No BBPS consumer cards for this customer." />
          ) : (
            <div className="surface-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="p-3 pl-5">Card / loan</th>
                    <th className="p-3">Number</th>
                    <th className="p-3">Cardholder</th>
                    <th className="p-3">Biller</th>
                    <th className="p-3">Last bill</th>
                    <th className="p-3">Retailer</th>
                    <th className="p-3 text-right pr-5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.consumer_cards.map((c) => (
                    <tr key={c.id} className="border-b border-border/50 last:border-0 hover:bg-accent/30" data-testid={`card-row-${c.id}`}>
                      <td className="p-3 pl-5">
                        <div className="flex items-center gap-2">
                          {c.category === "credit_card" ? <CreditCard className="h-4 w-4 text-primary" /> : <Banknote className="h-4 w-4 text-secondary" />}
                          <span className="font-medium">{c.nickname}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">{c.category}</div>
                      </td>
                      <td className="p-3 font-mono text-xs">{(c.consumer_id || "").replace(/(\d{4})/g, "$1 ").trim()}</td>
                      <td className="p-3 text-xs">{c.consumer_name}</td>
                      <td className="p-3 text-xs">{c.biller_name}</td>
                      <td className="p-3 text-xs">
                        {c.last_bill_amount != null ? (
                          <>
                            <div className="font-mono">{formatINR(c.last_bill_amount)}</div>
                            {c.last_bill_due_date && <div className="text-[10px] text-muted-foreground">due {c.last_bill_due_date}</div>}
                          </>
                        ) : <em className="text-muted-foreground">—</em>}
                      </td>
                      <td className="p-3 text-xs">{c.retailer ? <>{c.retailer.full_name}<div className="text-[10px] text-muted-foreground">{c.retailer.email}</div></> : "—"}</td>
                      <td className="p-3 pr-5 text-right">
                        <Button size="sm" variant="ghost" onClick={() => deleteCard(c.id)} disabled={actingId === c.id}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-card-${c.id}`}>
                          {actingId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="wallet" className="mt-6">
          {(!data.wallet_ledger || data.wallet_ledger.length === 0) ? (
            <Empty icon={Wallet} text="No wallet ledger entries yet." />
          ) : (
            <div className="surface-card overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="p-3 pl-5">When</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Amount</th>
                    <th className="p-3">Balance after</th>
                    <th className="p-3">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {data.wallet_ledger.map((l) => (
                    <tr key={l.id} className="border-b border-border/50 last:border-0 hover:bg-accent/30">
                      <td className="p-3 pl-5 text-xs whitespace-nowrap">{formatTime(l.created_at)}</td>
                      <td className="p-3 text-xs">
                        <Badge variant="outline" className={`text-[10px] ${l.kind === "credit" ? "border-success/40 text-success bg-success/5" : "border-destructive/40 text-destructive bg-destructive/5"}`}>
                          {l.kind || l.type || "—"}
                        </Badge>
                      </td>
                      <td className="p-3 font-mono">{formatINR(l.amount)}</td>
                      <td className="p-3 font-mono text-xs text-muted-foreground">{l.balance_after != null ? formatINR(l.balance_after) : "—"}</td>
                      <td className="p-3 text-xs truncate max-w-[280px]">{l.note || l.reference || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="txns" className="mt-6">
          {(!data.transactions || data.transactions.length === 0) ? (
            <Empty icon={Activity} text="No transactions for this customer." />
          ) : (
            <div className="surface-card overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="p-3 pl-5">When</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Category / rail</th>
                    <th className="p-3">Amount</th>
                    <th className="p-3">Display ID</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((t) => (
                    <tr key={t.id} className="border-b border-border/50 last:border-0 hover:bg-accent/30">
                      <td className="p-3 pl-5 text-xs whitespace-nowrap">{formatTime(t.created_at)}</td>
                      <td className="p-3 text-xs"><Badge variant="outline" className="text-[10px]">{t.type}</Badge></td>
                      <td className="p-3 text-xs"><Badge variant="outline" className="text-[10px]">{t.status}</Badge></td>
                      <td className="p-3 text-xs">{t.category || t.rail || "—"}</td>
                      <td className="p-3 font-mono">{formatINR(t.amount)}</td>
                      <td className="p-3 font-mono text-[10px] text-muted-foreground">{t.display_id || t.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="topups" className="mt-6">
          {(!data.topups || data.topups.length === 0) ? (
            <Empty icon={Wallet} text="No wallet top-ups for this customer." />
          ) : (
            <div className="surface-card overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="p-3 pl-5">When</th>
                    <th className="p-3">Gateway</th>
                    <th className="p-3">Channel</th>
                    <th className="p-3">Amount</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Pay mode</th>
                    <th className="p-3">Order ID</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topups.map((t) => (
                    <tr key={t.id} className="border-b border-border/50 last:border-0 hover:bg-accent/30">
                      <td className="p-3 pl-5 text-xs whitespace-nowrap">{formatTime(t.created_at)}</td>
                      <td className="p-3 text-xs">{t.gateway}</td>
                      <td className="p-3 text-xs">{t.pg || "—"}</td>
                      <td className="p-3 font-mono">{formatINR(t.amount)}</td>
                      <td className="p-3 text-xs"><Badge variant="outline" className="text-[10px]">{t.status}</Badge></td>
                      <td className="p-3 text-xs">{t.payment_mode_type || "—"}</td>
                      <td className="p-3 font-mono text-[10px] text-muted-foreground">{t.provider_order_id || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="mt-6 rounded-md border border-warning/30 bg-warning/5 p-3 text-xs flex items-start gap-2">
        <AlertCircle className="h-4 w-4 text-warning mt-0.5" />
        <div>
          <div className="font-semibold">Admin override:</div> Deletes performed here are immediate, audited and irreversible from the UI. Retailers will lose access to the deleted record on their next session refresh.
        </div>
      </div>
    </div>
  );
}

const StatPill = ({ icon: Icon, label, value }) => (
  <div className="rounded-md border border-border bg-card px-3 py-2 flex items-center gap-2">
    <Icon className="h-4 w-4 text-primary" strokeWidth={1.5} />
    <div className="text-[11px] text-muted-foreground">{label}</div>
    <div className="text-sm font-mono font-bold">{value}</div>
  </div>
);

const Empty = ({ icon: Icon, text }) => (
  <div className="surface-card p-12 text-center">
    <Icon className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
    <p className="mt-3 text-sm text-muted-foreground">{text}</p>
  </div>
);

function formatTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/**
 * Phase 7.30h — Unified BBPS + wallet + top-up timeline for a single customer.
 *
 * Merges three datasets the admin Customer-detail endpoint already returns:
 *   - data.wallet_ledger  (credit / debit on customer wallet)
 *   - data.transactions   (BBPS payments where customer was the payee)
 *   - data.topups         (Razorpay / Cashfree / etc. order objects)
 *
 * Each is normalized into a row {kind, when, label, amount, sign, status,
 * ref, retailer} and sorted descending by `when`. No new API calls.
 */
function CombinedTimeline({ data }) {
  const rows = React.useMemo(() => {
    const out = [];
    for (const l of (data?.wallet_ledger || [])) {
      const isCredit = (l.type || "").toLowerCase() === "credit";
      out.push({
        key: `lg_${l.id || l.created_at}`,
        kind: isCredit ? "Wallet credit" : "Wallet debit",
        when: l.created_at,
        label: l.reason || (isCredit ? "Wallet credit" : "Wallet debit"),
        amount: Number(l.amount || 0),
        sign: isCredit ? "+" : "−",
        balance_after: l.balance_after,
        status: "success",
        ref: l.ref_id,
        ref_type: l.ref_type,
      });
    }
    for (const t of (data?.transactions || [])) {
      out.push({
        key: `txn_${t.id}`,
        kind: "BBPS payment",
        when: t.created_at,
        label: `${t.category || "BBPS"} · ${t.consumer_name || t.consumer_id || ""}`.trim(),
        amount: Number(t.amount || 0),
        sign: "−",
        status: t.status,
        ref: t.provider_ref || t.bank_utr || t.display_id || t.id,
        retailer_id: t.user_id,
      });
    }
    for (const u of (data?.topups || [])) {
      out.push({
        key: `top_${u.id}`,
        kind: "Top-up",
        when: u.created_at,
        label: `Top-up · ${u.gateway || u.method || "—"}`,
        amount: Number(u.amount || 0),
        sign: u.status === "success" ? "+" : "·",
        status: u.status,
        ref: u.provider_ref || u.pg_ref || u.provider_order_id || u.id,
      });
    }
    out.sort((a, b) => (b.when || "").localeCompare(a.when || ""));
    return out;
  }, [data]);

  if (!rows.length) {
    return <Empty icon={Activity} text="No activity for this customer yet." />;
  }
  return (
    <div className="surface-card overflow-hidden" data-testid="combined-timeline">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>When</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Balance after</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Ref</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key} data-testid={`timeline-row-${r.key}`}>
              <TableCell className="text-xs whitespace-nowrap">{formatTime(r.when)}</TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={`text-[10px] ${
                    r.kind === "Wallet credit" || r.kind === "Top-up"
                      ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                      : r.kind === "Wallet debit" || r.kind === "BBPS payment"
                      ? "border-amber-300 text-amber-700 bg-amber-50"
                      : ""
                  }`}
                >
                  {r.kind}
                </Badge>
              </TableCell>
              <TableCell className="text-sm">{r.label}</TableCell>
              <TableCell className="text-right font-mono tabular-nums">
                <span className={r.sign === "+" ? "text-emerald-700" : r.sign === "−" ? "text-amber-700" : ""}>
                  {r.sign}{formatINR(r.amount)}
                </span>
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                {r.balance_after != null ? formatINR(r.balance_after) : "—"}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={`text-[10px] ${
                  r.status === "success" ? "border-emerald-300 text-emerald-700" :
                  r.status === "failed" || r.status === "rejected" ? "border-destructive/40 text-destructive" :
                  "border-amber-300 text-amber-700"
                }`}>{r.status}</Badge>
              </TableCell>
              <TableCell className="text-xs font-mono truncate max-w-[160px]" title={r.ref}>
                {r.ref ? String(r.ref).slice(0, 22) : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
